/**
 * Amazon Ads SP fetchMetrics orchestration (Story 19.14, Task 5)
 *
 * The adapter's `fetchMetrics` entry point delegates to `runSpFetchMetrics`
 * here. Centralising the 16-step orchestration in a dedicated module keeps
 * `amazon-ads.ts` focused on the adapter contract while letting the heavy
 * lifting be unit-tested in isolation.
 *
 * Steps (matches Story 19.14 Task 5.2 numbering):
 *
 *   1. Parse + validate request shape.
 *   2. Resolve regional host from `integration.region`.
 *   3. `pickReportType(dimensions)` — bail with `invalid_request` on unsupported combos.
 *   4. Build the v3 report body + compute request_hash.
 *   5. KV cache lookup (cross-tenant key). Hit → return cached records.
 *   6. Chunking via shared `chunkAndAggregate` if range > 31 days.
 *   7. In-flight dedup via `findInFlightJobByRequestHash`.
 *   8. 12-row safeguard via `countInFlightJobsForIntegration`.
 *   9. Submit POST + persist to D1 BEFORE polling.
 *  10. Poll loop with 1s/2s/4s/8s/8s/8s cadence + 30s wall-clock.
 *  11. Download + SSRF allowlist + 50MB cap + line-parse NDJSON.
 *  12. Column-drift detection.
 *  13. Apply derived metrics (CTR, ACOS, ROAS).
 *  14. Cache write (validated payload, dynamic TTL).
 *  15. Mark D1 row COMPLETED.
 *  16. Return AdapterMetrics with `dataFreshnessLagHours: 1`.
 */

import {
  adapterErr,
  adapterOk,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type OAuthCredentials,
} from "../adapter";
import {
  AMAZON_ADS_SP_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_SP_MAX_REPORTS_PER_PROFILE,
  AMAZON_ADS_SP_POLL_CADENCE_MS,
  AMAZON_ADS_SP_REPORT_TIMEOUT_MS,
  AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES,
  AMAZON_ADS_SP_PENDING_STATUSES,
  applyDerivedMetrics,
  buildAmazonAdsSpCacheKey,
  buildSpReportRequest,
  computeAmazonAdsSpCacheTtl,
  computeRequestHash,
  detectColumnDrift,
  downloadAndParseReport,
  normalizeRequestForHash,
  parseSpReportError,
  pollReport,
  SpReportApiError,
  submitSponsoredProductsReport,
  validateCachePayload,
  type SpReportRecord,
} from "./reports";
import {
  buildColumnList,
  pickReportType,
  type SpReportTypeId,
} from "./sp-column-map";
import {
  chunkAndAggregate,
  type ChunkResult,
} from "../async-chunking";
import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
} from "./oauth";

// ============================================================================
// Types
// ============================================================================

/**
 * D1 helper bag — passed in so the orchestration can stay agnostic to whether
 * the caller is the adapter, a queue worker, or the reaper.
 */
export interface SpReportJobsApi {
  insert(args: {
    id: string;
    report_id: string;
    tenant_id: string;
    integration_id: string;
    request_hash: string;
    correlation_id: string;
    /**
     * Story 19.16 Task 12 / AC #7 — forensic ad-product enrichment.
     * Optional only for back-compat with 19.14/19.15 callers; new DSP rows
     * MUST populate this so the phantom-report reaper can emit per-product
     * Grafana metrics. The shared orchestration in `report-orchestration.ts`
     * always passes this value.
     */
    ad_product?: "SPONSORED_PRODUCTS" | "SPONSORED_BRANDS" | "SPONSORED_DISPLAY" | "DSP";
    now?: number;
  }): Promise<void>;
  findInFlightByHash(args: {
    integration_id: string;
    request_hash: string;
    now?: number;
  }): Promise<{
    id: string;
    report_id: string;
    status: string;
  } | null>;
  countInFlight(args: { integration_id: string; now?: number }): Promise<number>;
  markStatus(args: {
    id: string;
    status:
      | "PENDING"
      | "PROCESSING"
      | "COMPLETED"
      | "FAILED"
      | "CANCELLED"
      | "reaper_failed"
      | "cancelled_validation_probe";
    completed_at?: number | null;
  }): Promise<{ updated: boolean }>;
}

/**
 * KV helper bag — minimal interface against `KVStore` from `cache.ts`.
 */
export interface SpKvApi {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, ttlSec: number): Promise<void>;
}

export interface RunSpFetchMetricsInput {
  readonly ctx: AdapterContext;
  readonly credentials: OAuthCredentials;
  readonly dateRange: { start: Date; end: Date };
  readonly integration: {
    readonly accountId: string; // primary profile ID
    readonly region: AmazonAdsRegion;
  };
  /** LWA client ID from env. */
  readonly lwaClientId: string;
  /** Selected metric keys (defaults to all 30). */
  readonly metrics?: ReadonlyArray<string>;
  /** Selected dimension keys. */
  readonly dimensions?: ReadonlyArray<string>;
  /** Persistence + KV bags. */
  readonly jobs: SpReportJobsApi;
  readonly kv: SpKvApi;
  /** Override fetch (for tests). */
  readonly fetchImpl?: typeof fetch;
  /** Override Date.now (for tests). */
  readonly now?: () => number;
  /** Override sleep (for tests). */
  readonly sleep?: (ms: number) => Promise<void>;
  /** Override correlation ID (for tests). */
  readonly correlationId?: string;
  /** Override report-job ID generator (for tests). */
  readonly newJobId?: () => string;
}

const DEFAULT_METRIC_KEYS = [
  "impressions",
  "clicks",
  "cost",
  "clickThroughRate",
  "costPerClick",
  "purchases7d",
  "purchases14d",
  "unitsSoldClicks7d",
  "unitsSoldClicks14d",
  "sales1d",
  "sales7d",
  "sales14d",
  "sales30d",
  "acosClicks7d",
  "acosClicks14d",
  "roasClicks7d",
  "roasClicks14d",
  "purchaseClickRate7d",
] as const;

// ============================================================================
// Top-level orchestration
// ============================================================================

export async function runSpFetchMetrics(
  input: RunSpFetchMetricsInput
): Promise<AdapterResult<AdapterMetrics>> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const now = input.now ?? Date.now;
  const sleep =
    input.sleep ??
    ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const correlationId = input.correlationId ?? crypto.randomUUID();
  const newJobId = input.newJobId ?? (() => crypto.randomUUID());

  // Step 1 — request validation.
  const valid = validateRequest(input);
  if (!valid.ok) return adapterErr(valid.error);

  // Step 2 — region.
  if (!AMAZON_ADS_REGIONAL_HOSTS[input.integration.region]) {
    return adapterErr({
      code: "invalid_response",
      message: `Unknown Amazon Ads region: ${input.integration.region}`,
      retryable: false,
    });
  }

  // Step 3 — pick report type.
  const dimensions = input.dimensions ?? [];
  const reportType = pickReportType(dimensions);
  if (!reportType.ok) {
    return adapterErr({
      code: "invalid_response",
      message: reportType.message,
      retryable: false,
    });
  }

  // Step 6 — chunking (sequential per chunk).
  const totalDays = daysInclusive(input.dateRange.start, input.dateRange.end);
  const startTime = now();
  const remainingBudgetMs = (): number =>
    Math.max(0, AMAZON_ADS_SP_REPORT_TIMEOUT_MS - (now() - startTime));

  const allRecords: SpReportRecord[] = [];
  const allWarnings: string[] = [];

  if (totalDays > AMAZON_ADS_SP_MAX_DAYS_PER_REPORT) {
    const chunkResult = await chunkAndAggregate<SpReportRecord>(
      { startDate: input.dateRange.start, endDate: input.dateRange.end },
      {
        maxDaysPerChunk: AMAZON_ADS_SP_MAX_DAYS_PER_REPORT,
        timeBudgetMs: AMAZON_ADS_SP_REPORT_TIMEOUT_MS,
      },
      (chunk) =>
        runOneChunk({
          input,
          fetchImpl,
          now,
          sleep,
          correlationId,
          newJobId,
          reportTypeId: reportType.reportTypeId,
          chunk,
          remainingBudgetMs,
        }),
      now
    );

    if (!chunkResult.success) {
      return adapterErr({
        code: (chunkResult.errorCode as never) ?? "invalid_response",
        message: chunkResult.errorMessage,
        retryable: false,
      });
    }
    if (chunkResult.partial) {
      // Story 19.14 AC #7: partial → retryable warning.
      allWarnings.push(
        `report_chunks_pending: ${chunkResult.chunksCompleted}/${chunkResult.chunksTotal} chunks completed`
      );
    }
    for (const r of chunkResult.records) allRecords.push(r);
    for (const w of chunkResult.warnings) allWarnings.push(w);
  } else {
    const r = await runOneChunk({
      input,
      fetchImpl,
      now,
      sleep,
      correlationId,
      newJobId,
      reportTypeId: reportType.reportTypeId,
      chunk: input.dateRange,
      remainingBudgetMs,
    });
    if (r.kind === "error" && r.fatal) {
      return adapterErr({
        code: (r.code as never) ?? "invalid_response",
        message: r.message,
        retryable: false,
      });
    }
    if (r.kind === "ok") {
      for (const rec of r.records) allRecords.push(rec);
      if (r.warnings) for (const w of r.warnings) allWarnings.push(w);
    } else {
      allWarnings.push(`single-chunk non-fatal: ${r.message}`);
    }
  }

  // Step 13 — derived metrics on aggregated records.
  const finalRecords = applyDerivedMetrics(allRecords);

  // Step 16 — return AdapterMetrics with dataFreshnessLagHours: 1.
  return adapterOk({
    platform: "amazon-ads",
    accountId: input.integration.accountId,
    dateRange: input.dateRange,
    records: finalRecords,
    fetchedAt: new Date(now()),
    recordCount: finalRecords.length,
    warnings: allWarnings.map((reason) => ({
      reportType: reportType.reportTypeId,
      reason,
    })),
    metadata: {
      dataFreshnessLagHours: 1,
      correlationId,
    },
  });
}

// ============================================================================
// Single-chunk execution
// ============================================================================

interface RunOneChunkArgs {
  readonly input: RunSpFetchMetricsInput;
  readonly fetchImpl: typeof fetch;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  readonly correlationId: string;
  readonly newJobId: () => string;
  readonly reportTypeId: SpReportTypeId;
  readonly chunk: { start: Date; end: Date } | { startDate: Date; endDate: Date };
  readonly remainingBudgetMs: () => number;
}

async function runOneChunk(args: RunOneChunkArgs): Promise<ChunkResult<SpReportRecord>> {
  const start = "start" in args.chunk ? args.chunk.start : args.chunk.startDate;
  const end = "end" in args.chunk ? args.chunk.end : args.chunk.endDate;
  const metrics = args.input.metrics ?? DEFAULT_METRIC_KEYS;
  const dimensions = args.input.dimensions ?? [];

  // Step 4 — build request body + hash.
  const { body, warnings: buildWarnings } = buildSpReportRequest({
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
  });
  const normalized = normalizeRequestForHash(body);
  const requestHash = await computeRequestHash(normalized);
  // Re-build with hash short suffix for grep-able report name.
  const { body: namedBody } = buildSpReportRequest({
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
    requestHashShort: requestHash.slice(0, 8),
  });

  // Step 5 — KV cache lookup.
  const cacheKey = buildAmazonAdsSpCacheKey({
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    requestHash,
  });

  try {
    const cached = await args.input.kv.get(cacheKey);
    if (cached) {
      const parsed = safeJson(cached);
      if (parsed && Array.isArray(parsed.records)) {
        return {
          kind: "ok",
          records: parsed.records as SpReportRecord[],
          warnings: ["cache_hit", ...buildWarnings],
        };
      }
    }
  } catch (err) {
    // Fail-open on KV read — fall through to API path.
    logEvent("amazon_ads_cache_read_failed", {
      correlationId: args.correlationId,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  // Step 7 — in-flight dedup.
  const existing = await args.input.jobs.findInFlightByHash({
    integration_id: args.input.ctx.integrationId,
    request_hash: requestHash,
    now: args.now(),
  });

  let jobId: string;
  let reportId: string;
  if (existing) {
    jobId = existing.id;
    reportId = existing.report_id;
    logEvent("amazon_ads_report_resumed", {
      correlationId: args.correlationId,
      reportId,
    });
  } else {
    // Step 8 — concurrent-cap safeguard.
    const inFlight = await args.input.jobs.countInFlight({
      integration_id: args.input.ctx.integrationId,
      now: args.now(),
    });
    if (inFlight >= AMAZON_ADS_SP_MAX_REPORTS_PER_PROFILE) {
      return {
        kind: "error",
        message: `Amazon Ads in-flight report cap reached (${inFlight}/${AMAZON_ADS_SP_MAX_REPORTS_PER_PROFILE} reserved). Try again in 30 seconds.`,
        code: "rate_limited",
        fatal: true,
      };
    }

    // Step 9 — submit POST + persist BEFORE polling.
    let submission;
    try {
      submission = await submitSponsoredProductsReport(
        {
          accessToken: args.input.credentials.accessToken,
          clientId: args.input.lwaClientId,
          profileId: args.input.integration.accountId,
          region: args.input.integration.region,
          body: namedBody,
        },
        args.fetchImpl
      );
    } catch (err) {
      // AC #11 — map provider error envelopes to the right adapter codes.
      if (err instanceof SpReportApiError) {
        const parsed = parseSpReportError(err);
        if (parsed.logEvent) {
          logEvent(parsed.logEvent, {
            correlationId: args.correlationId,
            providerStatus: err.status,
          });
        }
        return {
          kind: "error",
          message: err.message,
          code: parsed.code,
          fatal: !parsed.retryable,
        };
      }
      return {
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
        code: "invalid_response",
        fatal: true,
      };
    }
    reportId = submission.reportId;
    jobId = args.newJobId();
    await args.input.jobs.insert({
      id: jobId,
      report_id: reportId,
      tenant_id: args.input.ctx.tenantId,
      integration_id: args.input.ctx.integrationId,
      request_hash: requestHash,
      correlation_id: args.correlationId,
      now: args.now(),
    });
    logEvent("amazon_ads_report_submitted", {
      correlationId: args.correlationId,
      reportId,
    });
  }

  // Step 10 — poll loop with cadence + remaining wall-clock budget.
  let downloadUrl: string | undefined;
  let pollIteration = 0;
  let consecutivePollErrors = 0;
  let lastPollError: { code: string; message: string; retryable: boolean } | null =
    null;
  const MAX_CONSECUTIVE_POLL_ERRORS = 3;

  for (const cadenceMs of AMAZON_ADS_SP_POLL_CADENCE_MS) {
    const budget = args.remainingBudgetMs();
    if (budget <= 0) break;
    await args.sleep(Math.min(cadenceMs, budget));
    pollIteration++;
    let pollResult;
    try {
      pollResult = await pollReport(
        {
          accessToken: args.input.credentials.accessToken,
          clientId: args.input.lwaClientId,
          profileId: args.input.integration.accountId,
          region: args.input.integration.region,
          reportId,
        },
        args.fetchImpl
      );
      consecutivePollErrors = 0; // reset on success
    } catch (err) {
      consecutivePollErrors++;
      // AC #11 — map provider error envelopes; bail loud on non-retryable
      // (auth / permission / invalid request). Don't burn the wall-clock
      // budget retrying a token that's expired.
      if (err instanceof SpReportApiError) {
        const parsed = parseSpReportError(err);
        if (parsed.logEvent) {
          logEvent(parsed.logEvent, {
            correlationId: args.correlationId,
            providerStatus: err.status,
          });
        }
        lastPollError = {
          code: parsed.code,
          message: err.message,
          retryable: parsed.retryable,
        };
        if (!parsed.retryable) {
          // Bail loud — non-retryable poll errors mean the report is
          // permanently broken from this caller's perspective.
          await args.input.jobs.markStatus({
            id: jobId,
            status: "FAILED",
            completed_at: args.now(),
          });
          return {
            kind: "error",
            message: err.message,
            code: parsed.code,
            fatal: true,
          };
        }
      } else {
        lastPollError = {
          code: "network_error",
          message: err instanceof Error ? err.message : String(err),
          retryable: true,
        };
      }
      logEvent("amazon_ads_report_poll_error", {
        correlationId: args.correlationId,
        reportId,
        message: lastPollError.message,
        consecutiveErrors: consecutivePollErrors,
      });
      // Bail after N consecutive transient errors — don't burn entire
      // wall-clock budget pinging a flapping endpoint.
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        return {
          kind: "error",
          message: `${lastPollError.message} (after ${consecutivePollErrors} consecutive poll errors)`,
          code: lastPollError.code as "rate_limited" | "provider_unavailable",
          fatal: false, // retryable on next fetchMetrics invocation
        };
      }
      continue;
    }

    if (AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES.includes(pollResult.status)) {
      downloadUrl = pollResult.url;
      await args.input.jobs.markStatus({ id: jobId, status: "PROCESSING" });
      break;
    }
    if (pollResult.status === "FAILED") {
      await args.input.jobs.markStatus({
        id: jobId,
        status: "FAILED",
        completed_at: args.now(),
      });
      return {
        kind: "error",
        message: pollResult.failureReason ?? "Amazon Ads report FAILED",
        code: "invalid_response",
        fatal: true,
      };
    }
    if (pollResult.status === "CANCELLED") {
      await args.input.jobs.markStatus({
        id: jobId,
        status: "CANCELLED",
        completed_at: args.now(),
      });
      return {
        kind: "error",
        message: "Amazon Ads report CANCELLED externally",
        code: "invalid_response",
        fatal: true,
      };
    }
    if (!AMAZON_ADS_SP_PENDING_STATUSES.includes(pollResult.status)) {
      logEvent("amazon_ads_status_alias_observed", {
        correlationId: args.correlationId,
        reportId,
        status: pollResult.status,
      });
    }
  }

  if (!downloadUrl) {
    // Wall-clock exceeded — keep D1 row alive (do NOT mark terminal). Caller
    // sees a non-fatal warning + retries on next invocation.
    return {
      kind: "error",
      message: `report_pending_retry_later (poll iterations=${pollIteration})`,
      fatal: false,
    };
  }

  // Step 11 — download + decompress + parse.
  let parsed;
  try {
    parsed = await downloadAndParseReport(downloadUrl, args.fetchImpl);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("suspicious_download_url")) {
      logEvent("amazon_ads_suspicious_download_url", {
        correlationId: args.correlationId,
        reportId,
      });
    }
    if (msg.includes("report_oversized")) {
      logEvent("amazon_ads_oversized_report", {
        correlationId: args.correlationId,
        reportId,
      });
    }
    await args.input.jobs.markStatus({
      id: jobId,
      status: "FAILED",
      completed_at: args.now(),
    });
    return {
      kind: "error",
      message: msg,
      code: "invalid_response",
      fatal: true,
    };
  }

  const records = parsed.records;
  const warnings: string[] = [...buildWarnings, ...parsed.warnings];

  // Step 12 — column-drift detection.
  const requestedColumns = buildColumnList(
    args.reportTypeId,
    metrics,
    dimensions
  ).columns;
  const drift = detectColumnDrift(requestedColumns, records);
  if (drift.missingColumns.length > 0) {
    logEvent("amazon_ads_column_drift_detected", {
      correlationId: args.correlationId,
      reportId,
      missingColumns: drift.missingColumns,
    });
    for (const w of drift.warnings) warnings.push(w);
  }

  // Step 14 — cache write.
  const cacheValidation = validateCachePayload(records);
  if (cacheValidation.valid) {
    const ttlSec = computeAmazonAdsSpCacheTtl({
      startDate: start,
      endDate: end,
    }, new Date(args.now()));
    try {
      await args.input.kv.put(
        cacheKey,
        JSON.stringify({ records }),
        ttlSec
      );
    } catch (err) {
      logEvent("amazon_ads_cache_write_failed", {
        correlationId: args.correlationId,
        reportId,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  } else {
    logEvent("amazon_ads_cache_validation_failed", {
      correlationId: args.correlationId,
      reportId,
      reason: cacheValidation.reason,
    });
  }

  // Step 15 — mark COMPLETED.
  await args.input.jobs.markStatus({
    id: jobId,
    status: "COMPLETED",
    completed_at: args.now(),
  });
  logEvent("amazon_ads_report_completed", {
    correlationId: args.correlationId,
    reportId,
    recordCount: records.length,
  });

  return { kind: "ok", records, warnings };
}

// ============================================================================
// Validation + helpers
// ============================================================================

function validateRequest(
  input: RunSpFetchMetricsInput
):
  | { ok: true }
  | {
      ok: false;
      error: { code: "invalid_response"; message: string; retryable: false };
    } {
  if (
    !(input.dateRange.start instanceof Date) ||
    !(input.dateRange.end instanceof Date) ||
    Number.isNaN(input.dateRange.start.getTime()) ||
    Number.isNaN(input.dateRange.end.getTime())
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SP fetchMetrics: dateRange contains invalid Date",
        retryable: false,
      },
    };
  }
  if (input.dateRange.start.getTime() > input.dateRange.end.getTime()) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SP fetchMetrics: dateRange.start > dateRange.end",
        retryable: false,
      },
    };
  }
  if (
    !input.integration.accountId ||
    typeof input.integration.accountId !== "string"
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SP fetchMetrics: integration.accountId (profile ID) is required",
        retryable: false,
      },
    };
  }
  if (!input.lwaClientId) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SP fetchMetrics: AMAZON_ADS_CLIENT_ID env binding missing",
        retryable: false,
      },
    };
  }
  // Cache key includes tenantId + integrationId (Round 3 Vuln 4 — cross-tenant
  // cache poisoning is the highest-blast-radius failure for 19.14). Validating
  // up-front means an empty value fails LOUD at request-time rather than deep
  // inside the chunk runner where the throw would propagate as an unhandled
  // error.
  if (
    typeof input.ctx.tenantId !== "string" ||
    input.ctx.tenantId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SP fetchMetrics: ctx.tenantId is required (cross-tenant cache isolation, Round 3 Vuln 4)",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.integrationId !== "string" ||
    input.ctx.integrationId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SP fetchMetrics: ctx.integrationId is required",
        retryable: false,
      },
    };
  }
  return { ok: true };
}

function daysInclusive(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

function safeJson(s: string): { records?: unknown } | null {
  try {
    return JSON.parse(s) as { records?: unknown };
  } catch {
    return null;
  }
}

function logEvent(event: string, details: Record<string, unknown>): void {
  // Privacy: NEVER log raw access token / refresh token / S3 path component.
  // Caller is responsible for not passing sensitive values into `details`.
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
